import type { JSX } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";

import PasswordInput from "src/components/common/PasswordInput";
import { useChangePassword } from "src/hooks/useAuth";
import { ChangePasswordSchema } from "src/validation/changePassword.schema";
import type { ChangePasswordFormType } from "src/types/changePassword";

export default function ChangePassword(): JSX.Element {
  const navigate = useNavigate();
  const { mutate: changePassword, isPending } = useChangePassword();

  const { control, handleSubmit, reset, trigger } =
    useForm<ChangePasswordFormType>({
      mode: "onBlur",
      resolver: zodResolver(ChangePasswordSchema),
      defaultValues: {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
    });

  const onSubmit = (values: ChangePasswordFormType) => {
    changePassword(values, { onSuccess: () => reset() });
  };

  return (
      <div className="fade modal show d-block">
          <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg">
                  <div className="modal-header d-block">
                      <div className="modal-title h4">Change Password</div>
                      <p className="text-priwinkle m-0">New password can not be same as current password. You'll stay signed in on this device.</p>
                  </div>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                      <div className="modal-body">
                          <Controller
                              name="currentPassword"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                  <Form.Group className="mb-4" controlId="currentPassword">
                                      <Form.Label className="mb-2">Current Password</Form.Label>
                                      <PasswordInput
                                          id="currentPassword"
                                          {...field}
                                          onChange={(value) => {
                                              field.onChange(value);
                                              // "New password must differ from current
                                              // password" is a cross-field zod refine
                                              // attached to newPassword's error path —
                                              // editing currentPassword alone doesn't
                                              // re-run it, so a stale error there could
                                              // survive after they no longer match.
                                              void trigger("newPassword");
                                          }}
                                          error={error?.message}
                                          placeholder="Enter current password"
                                          disabled={isPending}
                                          autoComplete="current-password"
                                          autoFocus
                                      />
                                  </Form.Group>
                              )}
                          />

                          <Controller
                              name="newPassword"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                  <Form.Group className="mb-4" controlId="newPassword">
                                      <Form.Label className="mb-2">New Password</Form.Label>
                                      <PasswordInput
                                          id="newPassword"
                                          {...field}
                                          onChange={(value) => {
                                              field.onChange(value);
                                              // "Passwords do not match" is a cross-field
                                              // zod refine attached to confirmPassword's
                                              // error path — editing newPassword alone
                                              // doesn't re-run it, so a stale mismatch
                                              // error could survive after they now match.
                                              void trigger("confirmPassword");
                                          }}
                                          error={error?.message}
                                          placeholder="Enter new password"
                                          disabled={isPending}
                                      />
                                  </Form.Group>
                              )}
                          />

                          <Controller
                              name="confirmPassword"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                  <Form.Group className="mb-4" controlId="confirmPassword">
                                      <Form.Label className="mb-2">Confirm Password</Form.Label>
                                      <PasswordInput
                                          id="confirmPassword"
                                          {...field}
                                          error={error?.message}
                                          placeholder="Re-enter new password"
                                          disabled={isPending}
                                      />
                                  </Form.Group>
                              )}
                          />
                      </div>
                      <div className="modal-footer">
                          <div className="d-flex justify-content-end gap-3">
                              <Button
                                  variant="outline-secondary"
                                  type="button"
                                  disabled={isPending}
                                  onClick={() => navigate(-1)}
                              >
                                  Cancel
                              </Button>
                              <Button variant="primary" type="submit" disabled={isPending}>
                                  {isPending ? (
                                      <>
                                          <Spinner animation="border" size="sm" className="me-1" />
                                          Updating...
                                      </>
                                  ) : (
                                      "Update Password"
                                  )}
                              </Button>
                          </div>
                      </div>
                  </Form>
              </div>
          </div>
      </div>
  );
}
